import {
  DEFAULT_INVALIDATE_OPTIONS,
  DEFAULT_QUERY_OPTIONS,
  NON_INVALIDATING_QUERY_OPTIONS,
  useGetOne,
  UseMutationCallbackType,
  UseQueryOptionsType,
} from "#hooks/adapters/adapterUtils";
import { useAuthenticatedContext } from "#src/contexts/AuthenticatedContext.helpers";
import useLocalization from "#src/hooks/useLocalization";
import { ExceptionUtils } from "#src/utils/exception";
import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { PillProps, useToast } from "@validereinc/common-components";
import {
  FlowAdapter,
  FlowStatus,
  FlowStatusType,
  Resources,
  type FlowGetListRequestType,
} from "@validereinc/domain";
import { BaseError, dateFormatter, downloadLink } from "@validereinc/utilities";

export const FLOW_QUERY_KEY = "flows";

export const FlowStatusPillVariants: Record<
  FlowStatusType,
  PillProps["variant"]
> = {
  [FlowStatus.ACTIVE]: "success",
  [FlowStatus.INACTIVE]: "default",
};

export const useGetOneFlow = useGetOne(FlowAdapter.getOne, Resources.FLOW);

export const useListFlows = (
  apiParams: Parameters<typeof FlowAdapter.getList>[0] = {},
  options: {
    select?: any;
    enabled?: boolean;
  } = {}
) =>
  useQuery({
    queryKey: [FLOW_QUERY_KEY, "getList", apiParams] as const,
    queryFn: ({ queryKey: [_, __, params] }) => FlowAdapter.getList(params),
    ...DEFAULT_QUERY_OPTIONS,
    ...options,
  });

export const useGetManySingleUpstreamFlow = (
  { ids }: { ids: string[] },
  options: UseQueryOptionsType<
    Awaited<ReturnType<typeof FlowAdapter.getList>>
  > = {}
) =>
  useQueries({
    // @ts-expect-error TODO: useQuery option typing is wrong
    queries: ids.map((id) => {
      const params: FlowGetListRequestType = {
        filters: { "downstream_flows.id": id },
        pageSize: 1,
        page: 1,
      };
      return {
        queryKey: [FLOW_QUERY_KEY, "getList", params],
        queryFn: () => FlowAdapter.getList(params),
        select: (resp) => resp?.data[0],
        ...DEFAULT_QUERY_OPTIONS,
        ...options,
      };
    }),
  });

export const useGetManySingleDownstreamFlow = (
  { ids }: { ids: string[] },
  options: UseQueryOptionsType<
    Awaited<ReturnType<typeof FlowAdapter.getList>>
  > = {}
) =>
  useQueries({
    // @ts-expect-error TODO: useQuery option typing is wrong
    queries: ids.map((id) => {
      const params: FlowGetListRequestType = {
        filters: { "upstream_flows.id": id },
        pageSize: 1,
        page: 1,
      };
      return {
        queryKey: [FLOW_QUERY_KEY, "getList", params],
        queryFn: () => FlowAdapter.getList(params),
        select: (resp) => resp?.data[0],
        ...DEFAULT_QUERY_OPTIONS,
        ...options,
      };
    }),
  });

export const useCreateFlow = ({
  onSuccess,
}: UseMutationCallbackType<
  Awaited<ReturnType<typeof FlowAdapter.createOne>>
> = {}) => {
  const { toast } = useToast();
  const { invalidate } = useClearFlowsCache();

  return useMutation({
    mutationFn: (data: Parameters<typeof FlowAdapter.createOne>[0]) =>
      FlowAdapter.createOne(data),
    onSuccess: (data, variables, context) => {
      toast.push({
        intent: "success",
        description: "Successfully created flow",
      });
      invalidate();
      onSuccess?.(data, variables, context);
    },
    onError: () => {
      toast.push({
        intent: "error",
        description: "Failed to create flow",
      });
    },
  });
};

export const useUpdateFlow = ({
  onSuccess,
}: UseMutationCallbackType<
  Awaited<ReturnType<typeof FlowAdapter.updateOne>>
> = {}) => {
  const { toast } = useToast();
  const { invalidate } = useClearFlowsCache();
  const { localize } = useLocalization();

  return useMutation({
    mutationFn: (params: Parameters<typeof FlowAdapter.updateOne>[0]) =>
      FlowAdapter.updateOne(params),
    onSuccess: (data, variables, context) => {
      toast.push({
        intent: "success",
        description: `Successfully updated ${localize("flow")}.`,
      });
      invalidate();
      onSuccess?.(data, variables, context);
    },
    onError: () => {
      toast.push({
        intent: "error",
        description: `Failed to update ${localize("flow")}.`,
      });
    },
  });
};

export const useListFlowTypes = () =>
  useQuery({
    queryKey: [FLOW_QUERY_KEY, "types", "getList"],
    queryFn: () => FlowAdapter.types.getList({}),
    ...NON_INVALIDATING_QUERY_OPTIONS,
  });

export const useListFlowProductTypes = () =>
  useQuery({
    queryKey: [FLOW_QUERY_KEY, "productTypes", "getList"],
    queryFn: () => FlowAdapter.productTypes.getList({}),
    ...NON_INVALIDATING_QUERY_OPTIONS,
  });

export const useListFlowProductCategories = () =>
  useQuery({
    queryKey: [FLOW_QUERY_KEY, "productCategories", "getList"],
    queryFn: () => FlowAdapter.productCategories.getList({}),
    ...NON_INVALIDATING_QUERY_OPTIONS,
  });

export const useDeleteFlow = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: Parameters<typeof FlowAdapter.deleteOne>[0]) =>
      FlowAdapter.deleteOne(params),
    onSuccess: () => {
      toast.push({
        intent: "success",
        description: `Successfully deleted flow`,
      });
      return queryClient.invalidateQueries([FLOW_QUERY_KEY]);
    },
  });
};

export const useExportFlows = (
  apiParams: Parameters<typeof FlowAdapter.exportList>[0] = {}
) => {
  const { toast } = useToast();
  const {
    v2: { companyInfo },
  } = useAuthenticatedContext();

  const getFileName = () =>
    [companyInfo?.company?.name, "Flows_List", dateFormatter(new Date())]
      .filter((part) => !!part)
      .join("_");

  return useMutation({
    mutationFn: async () => {
      const report = await FlowAdapter.exportList(apiParams);
      if (!report?.s3_download_link) {
        throw new BaseError(`Could not fetch download URL for flows export`, {
          cause: report,
        });
      }
      downloadLink(report.s3_download_link, getFileName());
    },
    onError: (error) => {
      ExceptionUtils.reportException(error, "error");
      toast.push({
        intent: "error",
        description: "Failed to export flows list.",
      });
    },
  });
};

export const useClearFlowsCache = () => {
  const queryClient = useQueryClient();
  return {
    invalidate: () => {
      queryClient.invalidateQueries({
        queryKey: [FLOW_QUERY_KEY],
        ...DEFAULT_INVALIDATE_OPTIONS,
      });
    },
  };
};
